import { useTranslations } from 'next-intl'

import { TRoomDetail } from '@/entities/room'
import { cn, useFormatCurrency } from '@/shared/lib'
import { ContentBox, Notice, Separator } from '@/shared/ui'

type Props = Pick<
  TRoomDetail,
  'longTermDiscounts' | 'earlyCheckinDiscounts'
> & {
  className?: string
  highlightColor?: 'purple' | 'gray'
}
export const Discounts = ({
  longTermDiscounts,
  earlyCheckinDiscounts,
  highlightColor = 'purple',
  className,
}: Props) => {
  const hasLongTermDiscount = longTermDiscounts.length > 0
  const hasEarlyCheckinDiscount = earlyCheckinDiscounts.length > 0

  const hasAllDiscount = hasLongTermDiscount && hasEarlyCheckinDiscount
  const hasNoDiscount = !hasLongTermDiscount && !hasEarlyCheckinDiscount

  if (hasNoDiscount) {
    return null
  }

  return (
    <ContentBox
      color="light-gray"
      spacing="medium"
      className={cn('space-y-4', className)}
    >
      {hasLongTermDiscount && (
        <LongTermDiscount
          longTermDiscounts={longTermDiscounts}
          highlightColor={highlightColor}
        />
      )}

      {hasAllDiscount && <Separator color="light-gray" />}

      {hasEarlyCheckinDiscount && (
        <EarlyCheckinDiscount
          earlyCheckinDiscounts={earlyCheckinDiscounts}
          highlightColor={highlightColor}
        />
      )}
    </ContentBox>
  )
}

const LongTermDiscount = ({
  longTermDiscounts,
  highlightColor,
}: Pick<TRoomDetail, 'longTermDiscounts'> & Pick<Props, 'highlightColor'>) => {
  const t = useTranslations()
  const { formatCurrency } = useFormatCurrency()

  return (
    <div className="space-y-2">
      <h4 className="typo-label1 font-semibold text-black">
        {t('room_detail.lease_details.long_term_discount.title')}
      </h4>
      <Notice.List gap={false} className="space-y-1">
        {longTermDiscounts.map(
          ({ weeks, discountRate, discountedUsingFee }) => (
            <Notice.Item key={weeks} className="flex justify-between gap-2">
              <div className="typo-label2 font-normal text-gray-900">
                {t.rich(
                  'room_detail.lease_details.long_term_discount.description',
                  {
                    weeks,
                    discountRate,
                    strong: chunks => (
                      <span
                        className={cn(
                          'font-semibold break-keep',
                          highlightColor === 'purple' && 'text-purple-500',
                        )}
                      >
                        {chunks}
                      </span>
                    ),
                  },
                )}
              </div>
              <div className="typo-label2 flex h-fit items-center gap-1 font-normal">
                <span className="font-semibold text-black">
                  {formatCurrency(discountedUsingFee)}
                </span>
                <span className="typo-caption1 text-gray-200">/</span>
                <span className="text-gray-900">{t('common.week_unit')}</span>
              </div>
            </Notice.Item>
          ),
        )}
      </Notice.List>
    </div>
  )
}

const EarlyCheckinDiscount = ({
  earlyCheckinDiscounts,
  highlightColor,
}: Pick<TRoomDetail, 'earlyCheckinDiscounts'> &
  Pick<Props, 'highlightColor'>) => {
  const t = useTranslations()

  return (
    <div className="space-y-2">
      <h4 className="typo-label1 font-semibold text-black">
        {t('room_detail.lease_details.immediate_discount.title')}
      </h4>
      <Notice.List gap={false} className="space-y-1">
        {earlyCheckinDiscounts.map(({ days, discountAmount }) => {
          return (
            <Notice.Item
              key={days}
              className="flex items-center justify-between"
            >
              <div className="typo-label2 font-normal text-gray-900">
                {t.rich(
                  'room_detail.lease_details.immediate_discount.description',
                  {
                    days,
                    strong: chunks => (
                      <span
                        className={cn(
                          'font-semibold',
                          highlightColor === 'purple' && 'text-purple-500',
                        )}
                      >
                        {chunks}
                      </span>
                    ),
                  },
                )}
              </div>
              <span className="typo-label2 shrink-0 text-end font-semibold text-black">
                {t(
                  'room_detail.lease_details.immediate_discount.discount_amount',
                  {
                    discountAmount,
                  },
                )}
              </span>
            </Notice.Item>
          )
        })}
      </Notice.List>
    </div>
  )
}
